import { AuthProvider, ReauthError, ReauthErrorKind } from '@griply/types';
import { getAuthProvider, isRecentLogin, mapReauthError } from '@griply/utils';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { getErrorReporter } from 'shared/services/error-tracking';

import { getAuthentication } from './helpers';

// Deliberate platform parallel: mobile reauths via native Google/Apple modules
// on a sync auth instance, web via browser redirects on the async JS SDK — only
// the error mapping (mapReauthError) is shareable.
export const getCurrentUserAuthProvider =
  async (): Promise<AuthProvider | null> => {
    const auth = await getAuthentication();
    return getAuthProvider(auth.currentUser?.providerData[0]?.providerId);
  };

// Lets the delete flow skip its reauth step when the user signed in seconds ago
// — notably right after returning from the Google/Apple redirect, which would
// otherwise bounce them straight back out again.
export const isCurrentLoginRecent = async (now: Date): Promise<boolean> => {
  const auth = await getAuthentication();
  return isRecentLogin(auth.currentUser?.metadata.lastSignInTime, now);
};

export const reauthenticateWithPassword = async (password: string) => {
  const auth = await getAuthentication();
  const email = auth.currentUser?.email;
  if (!auth.currentUser || !email) {
    throw new ReauthError(ReauthErrorKind.NoUser);
  }

  const credential = EmailAuthProvider.credential(email, password);
  try {
    await reauthenticateWithCredential(auth.currentUser, credential);
  } catch (error: unknown) {
    const code = (error as { code?: string }).code ?? '';
    const kind = mapReauthError(code);
    if (kind === ReauthErrorKind.Unknown) {
      getErrorReporter().captureException(error, {
        tags: { source: 'firebase-reauthenticate-password' },
      });
    }
    throw new ReauthError(kind);
  }
};
